import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Card,
	Col,
	Row,
	Space,
	Switch,
	Typography,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faClock,
	faFlagCheckered,
	faPlay,
	faRoute,
} from '@fortawesome/pro-light-svg-icons';
import {
	Icon,
	IconProp,
} from '@fortawesome/fontawesome-svg-core';
import { Marker } from 'react-map-gl';
import { faAnchor } from '@fortawesome/pro-solid-svg-icons';
import { useQuery } from 'react-query';
import { faDroplet, faLeaf } from '@fortawesome/pro-regular-svg-icons';
import { formatDuration } from '@shared/utils/date';
import {
	ConsumptionSetAttachedTo,
	DATE_AND_TIME,
	MetersPer,
	PortActionTypes,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { round } from '@shared/utils/math';
import { formatNumber } from '@shared/utils/formatNumber';
import { Values } from '@shared/utils/objectEnums';
import isItemPortCall from '@shared/utils/isItemPortCall';
import { 
	getEuSeaPassageQualifyingPercent, 
	qualifiesForEuComplianceCosts 
} from '@shared/utils/emissionsCompliance/euCompliance/euComplianceCosts';
import { type ConsumptionSetProps } from '@api/models/consumption-set';
import { GetSeaPassageConsumptionResponse } from '@api/features/performance/getSeaPassageConsumption';
import NumericInput from '@client/components/NumericInput';
import RouteLayer from '@client/components/Map/layers/RouteLayer';
import Map from '@client/components/Map/Map';
import {
	getPortPurposes,
	getSeaPassageConsumption,
	updateSeaPassage,
	updateVoyageExpense,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import useDeepMemo from '@client/utils/hooks/useDeepMemo';
import PointLayer from '@client/components/Map/layers/PointLayer';
import { renderConsumptionSetOptions } from '@client/components/ConsumptionSet/renderConsumptionSetOptions';
import { formatDate } from '@client/utils/formatDate';
import EmptyText from '@client/components/EmptyText';
import Select from '@client/components/Select';
import { ItineraryPortCallDto, ItinerarySeaPassageDto } from '@client/screens/estimates/details/helpers/types';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import RequiredArrivalPopover from '@client/components/RequiredArrivalPopover/RequiredArrivalPopover';
import styles from '../ItineraryTab.module.css';
import { useItinerary } from '../ItineraryProvider';
import { useVoyage } from '../../VoyageProvider/VoyageProvider';
import EuComplianceApply from './EuComplianceApply';

const { LocalTime } = TimeZoneFormat;

type EuComplianceRow = {
	type: 'portCall' | 'seaPassage';
	sourceId: number;
	sourceIds?: number[];
	apply?: boolean;
};

const getApply = (
	rows: EuComplianceRow[] | undefined,
	type: EuComplianceRow['type'],
	sourceId: number,
) => {
	const matchingRows = rows?.filter((row) => (
		row.type === type &&
		(row.sourceIds ?? [row.sourceId]).includes(sourceId)
	)) ?? [];

	if (matchingRows.length === 0) {
		return true;
	}

	return matchingRows.some((row) => row.apply !== false);
};

const updateRowsApply = (
	rows: EuComplianceRow[],
	type: EuComplianceRow['type'],
	sourceId: number,
	apply: boolean,
) => rows.map((row) => (
	row.type === type &&
		(row.sourceIds ?? [row.sourceId]).includes(sourceId) ?
		{ ...row, apply } :
		row
));

const SeaPassageSummary = ({
	selectedEntry,
	refreshDetails,
	consumptionSets,
}: {
	selectedEntry: ItinerarySeaPassageDto;
	refreshDetails: () => void;
	consumptionSets: ConsumptionSetProps[] | undefined;
}) => {
	const {
		vendorExpenses,
		voyageDetails,
		refreshVendorExpenses,
		refreshPnl,
		refreshVoyageDetails,
	} = useVoyage();
	const { itinerary } = useItinerary();
	const [localSeaPassage, setLocalSeaPassage] = useState<ItinerarySeaPassageDto>(selectedEntry);
	const [pendingSeaPassageChanges, setPendingSeaPassageChanges] = useState<Partial<ItinerarySeaPassageDto>>({});
	const [seaPassageConsumption, setSeaPassageConsumption] = useState<GetSeaPassageConsumptionResponse>(null);
	const [requiredArrivalOpen, setRequiredArrivalOpen] = useState(false);

	useQuery(
		`${selectedEntry.id}`,
		() => getSeaPassageConsumption(selectedEntry.id),
		{
			onSuccess: (data) => setSeaPassageConsumption(data),
			enabled: selectedEntry != null,
		}
	);

	const {
		data: portPurposeList = [],
	} = useQuery(
		['portPurposes'],
		getPortPurposes,
		{
			staleTime: 0,
			refetchOnMount: 'always',
		},
	);

	const eligiblePortPurposes = useMemo(() => (
		portPurposeList
			.filter((item) => item.includeInEuComplianceCosts)
			.map((item) => item.purpose)
	), [portPurposeList]);

	const fromPortCall = useMemo(() => (
		itinerary?.find((item): item is ItineraryPortCallDto => (
			isItemPortCall(item) &&
			item.id === localSeaPassage.prevPortCallId
		))
	), [itinerary, localSeaPassage.prevPortCallId]);

	const toPortCall = useMemo(() => (
		itinerary?.find((item): item is ItineraryPortCallDto => (
			isItemPortCall(item) &&
			item.id === localSeaPassage.nextPortCallId
		))
	), [itinerary, localSeaPassage.nextPortCallId]);

	const portCallQualifies = useCallback((portCall?: ItineraryPortCallDto) => {
		if (portCall == null) {
			return false;
		}

		return portCall.PortCallActions.some((action) => (
			qualifiesForEuComplianceCosts(
				{
					type: action.event as Values<typeof PortActionTypes>,
					port: portCall.port,
				},
				eligiblePortPurposes,
			)
		));
	}, [eligiblePortPurposes]);

	const euCompliancePercent = useMemo(() => (
		getEuSeaPassageQualifyingPercent({
			fromEu: portCallQualifies(fromPortCall),
			toEu: portCallQualifies(toPortCall),
		})
	), [fromPortCall, toPortCall, portCallQualifies]);

	useEffect(() => {
		setLocalSeaPassage(selectedEntry);
		setSeaPassageSettings(selectedEntry.routeOptions);
	}, [selectedEntry]);

	const {
		route,
		departureDate,
		arrivalDate,
		nextPortCallId,
		departurePort,
		arrivalPort,
	} = localSeaPassage;

	const [seaPassageSettings, setSeaPassageSettings] = useState(localSeaPassage.routeOptions);
	const [routeLoading, setRouteLoading] = useState(false);

	const ballastLeg = nextPortCallId === null && localSeaPassage.departurePort != null;

	const availableSpeeds = (consumptionSets ?? [])
		.map((set) => set.ladenSpeed)
		.filter((speed): speed is number => Number.isFinite(speed) && speed > 0);

	const defaultMapPosition = useMemo(() => {
		return ballastLeg ? {
			longitude: departurePort?.longitude ?? 55.6,
			latitude: departurePort?.latitude ?? 12.5,
			zoom: 5,
		} : {
			longitude: route?.features?.[0]?.geometry.coordinates[0][0] ?? 55.6,
			latitude: route?.features?.[0]?.geometry.coordinates[0][1] ?? 12.5,
			zoom: 3,
		};
	}, [ballastLeg, departurePort?.latitude, departurePort?.longitude, route?.features]);

	const mapContent = useMemo(() => (ballastLeg ? (
		() => (
			<Marker
				key={localSeaPassage.departurePort?.id}
				longitude={localSeaPassage.departurePort!.longitude}
				latitude={localSeaPassage.departurePort!.latitude}
				offsetTop={-14}
				offsetLeft={-14}
			>
				<FontAwesomeIcon
					icon={faAnchor as Icon}
					color="cyan"
				/>
			</Marker>
		)
	) : (
		() => (
			<>
				{localSeaPassage != null && (
					<>
						{departurePort != null && (
							<PointLayer
								key={departurePort.name}
								id={`destination-${departurePort.name}`}
								name={departurePort.name}
								latitude={departurePort.latitude}
								longitude={departurePort.longitude}
								layerStyle={{
									paint: {
										'circle-color': '#7FFF00',
										'circle-radius': 17.5,
										'circle-opacity': 0.55,
									},
								}}
							/>
						)}
						{arrivalPort != null && (
							<PointLayer
								key={arrivalPort.name}
								id={`destination-${arrivalPort.name}`}
								name={arrivalPort.name}
								latitude={arrivalPort.latitude}
								longitude={arrivalPort.longitude}
								layerStyle={{
									paint: {
										'circle-color': '#FF0000',
										'circle-radius': 17.5,
										'circle-opacity': 0.55,
									},
								}}
							/>
						)}
						<RouteLayer
							id="route"
							data={selectedEntry.route}
							loading={false}
						/>
					</>
				)}
			</>
		)
	)), [ballastLeg, localSeaPassage, arrivalPort, departurePort]);

	let distanceNm = 0;
	let secaDistanceNm = 0;

	if (route?.properties != null) {
		distanceNm = round(route?.properties.distance / MetersPer.NAUTICAL_MILE, 0);
		secaDistanceNm = round(route?.properties.secaIntersection / MetersPer.NAUTICAL_MILE, 0);
	}

	const map = useDeepMemo(() => {
		return (route != null || ballastLeg) && (
			<Map
				defaultPosition={defaultMapPosition}
				enableLegend
				hideVesselAndCargoToggles
				legendShowWhat={{ ecaLayer: true }}
			>
				{mapContent}
			</Map>
		);
	}, [ballastLeg, defaultMapPosition, mapContent, route]);

	const onUpdateSeaPassage = async (setting: string, value: number | boolean | null) => {
		setSeaPassageSettings({
			...seaPassageSettings,
			[setting]: value
		});

		// @ts-ignore
		setLocalSeaPassage((prev) => {
			if (prev == null) {
				return null;
			}

			return {
				...prev,
				[setting]: value,
			};
		});

		setPendingSeaPassageChanges((prev) => {
			return { ...prev, [setting]: value };
		});
	};

	const onSaveChanges = useCallback(async () => {
		if (localSeaPassage == null) {
			return;
		}

		setRouteLoading(true);

		try {
			await updateSeaPassage(localSeaPassage.vesselId, localSeaPassage.id, pendingSeaPassageChanges, true);

			const cons = await getSeaPassageConsumption(localSeaPassage.id);
			setSeaPassageConsumption(cons);
			await refreshDetails();
			setPendingSeaPassageChanges({});
		} catch (e) {
			showErrorNotification('Could not update sea passage settings', e as Error);
		} finally {
			setRouteLoading(false);
		}
	}, [
		refreshDetails,
		seaPassageSettings,
		localSeaPassage.id,
		localSeaPassage.vesselId,
	]);

	const showNormalConsumption = seaPassageConsumption?.normal != null &&
		seaPassageConsumption?.normal.length > 0;
	const showEcaConsumption = seaPassageConsumption?.eca != null &&
		seaPassageConsumption?.eca.length > 0;

	const saveButton = useSaveButton({
		onSave: onSaveChanges,
		title: 'Save & Calculate',
		loading: routeLoading,
		hasPendingChanges: Object.keys(pendingSeaPassageChanges).length > 0,
	});

	const [localEuEtsApply, setLocalEuEtsApply] = useState<boolean | null>(null);
	const [localFuelEuApply, setLocalFuelEuApply] = useState<boolean | null>(null);
	const [updatingApply, setUpdatingApply] = useState<'euEts' | 'fuelEu' | null>(null);

	const sourceType: EuComplianceRow['type'] = 'seaPassage';
	const sourceId = localSeaPassage.id;

	const calculatedEuEtsApply = useMemo(() => {
		const expense = vendorExpenses?.rows.find((item) => item.isEuEts);

		return getApply(
			expense?.euCalculationRows as EuComplianceRow[] | undefined,
			sourceType,
			sourceId,
		);
	}, [vendorExpenses, sourceId]);

	const calculatedFuelEuApply = useMemo(() => {
		const expense = vendorExpenses?.rows.find((item) => item.isFuelEuPenalty);

		return getApply(
			expense?.fuelEuCalculationRows as EuComplianceRow[] | undefined,
			sourceType,
			sourceId,
		);
	}, [vendorExpenses, sourceId]);

	const euEtsApply = localEuEtsApply ?? calculatedEuEtsApply;
	const fuelEuApply = localFuelEuApply ?? calculatedFuelEuApply;

	const onApplyChange = async (
		costType: 'euEts' | 'fuelEu',
		apply: boolean,
	) => {
		setUpdatingApply(costType);

		try {
			if (costType === 'euEts') {
				const expense = vendorExpenses?.rows.find((item) => item.isEuEts);

				if (expense == null) {
					throw new Error('EU ETS expense is unavailable');
				}

				setLocalEuEtsApply(apply);

				await updateVoyageExpense(expense.id, {
					euCalculationRows: updateRowsApply(
						(expense.euCalculationRows ?? []) as EuComplianceRow[],
						sourceType,
						sourceId,
						apply,
					),
				});
			}

			if (costType === 'fuelEu') {
				const expense = vendorExpenses?.rows.find((item) => item.isFuelEuPenalty);

				if (expense == null) {
					throw new Error('FuelEU penalty expense is unavailable');
				}

				setLocalFuelEuApply(apply);

				await updateVoyageExpense(expense.id, {
					fuelEuCalculationRows: updateRowsApply(
						(expense.fuelEuCalculationRows ?? []) as EuComplianceRow[],
						sourceType,
						sourceId,
						apply,
					),
				});
			}

			await refreshDetails();
			await refreshVendorExpenses();
			await refreshPnl();
			await refreshVoyageDetails();
		} catch (e) {
			setLocalEuEtsApply(null);
			setLocalFuelEuApply(null);
			showErrorNotification('Could not update EU compliance apply', e as Error);
		} finally {
			setUpdatingApply(null);
		}
	};

	return (
		<Row style={{ padding: 8 }} gutter={[8, 8]}>
			<Col md={24} xl={8}>
				<Row gutter={[16, 16]}>
					<Col md={12} xl={24} xxl={12}>
						<div>
							<Space className={styles.infoWrapper}>
								<FontAwesomeIcon icon={faPlay as IconProp} className={styles.highlightIcon} />
								Departure
							</Space>
							<br />
							{departureDate != null && formatDate(departureDate, DATE_AND_TIME, true, LocalTime)}
						</div>
					</Col>
					<Col md={12} xl={24} xxl={12}>
						<div>
							<Space className={styles.infoWrapper}>
								<FontAwesomeIcon
									icon={faFlagCheckered as IconProp}
									className={styles.highlightIcon}
								/>
								Arrival
								<RequiredArrivalPopover
									open={requiredArrivalOpen}
									setOpen={setRequiredArrivalOpen}
									disabled={
										routeLoading ||
										!(localSeaPassage.estimated || localSeaPassage.inProgress) ||
										departureDate == null ||
										arrivalPort == null ||
										departurePort == null ||
										availableSpeeds.length === 0
									}
									timeZoneFormat={LocalTime}
									currentArrivalDate={arrivalDate ?? null}
									previousDepartureDate={departureDate ?? null}
									fromPort={departurePort!}
									toPort={arrivalPort!}
									vesselId={localSeaPassage.vesselId}
									voyageId={localSeaPassage.voyageId}
									currentConsumptionSetId={localSeaPassage.activeConsumptionSetId}
									attachedTo={ConsumptionSetAttachedTo.VOYAGE}
									options={seaPassageSettings}
									onApply={async ({ consumptionSetId }) => {
										await updateSeaPassage(
											localSeaPassage.vesselId,
											localSeaPassage.id,
											{
												activeConsumptionSetId: consumptionSetId,
											},
											true,
										);

										await refreshDetails();
									}}
								/>
							</Space>
							<br />
							{arrivalDate != null && formatDate(arrivalDate, DATE_AND_TIME, true, LocalTime)}
						</div>
					</Col>
					<Col md={12} xl={24} xxl={12}>
						<div>
							<Space className={styles.infoWrapper}>
								<FontAwesomeIcon icon={faClock as IconProp} className={styles.highlightIcon} />
								{`${localSeaPassage.estimated || localSeaPassage.inProgress ? 'Estimated ' : ''}Duration`}
							</Space>
							<br />
							{(arrivalDate != null && departureDate != null) ?
								formatDuration({ rawStart: departureDate, rawEnd: arrivalDate }) :
								'N/A'
							}
						</div>
					</Col>
					<Col md={12} xl={24} xxl={12}>
						<div>
							<Space className={styles.infoWrapper}>
								<FontAwesomeIcon icon={faRoute as IconProp} className={styles.highlightIcon} />
								Estimated Distance
							</Space>
							<br />
							{arrivalDate != null ? (
								<>
									<div>
										{`${formatNumber(distanceNm)} NM`}
									</div>
									<div className={styles.secaDistance}>
										{`${formatNumber(secaDistanceNm)} NM of which in ECA`}
									</div>
								</>
							) : 'N/A'}
						</div>
					</Col>
					<Col md={12} xl={24} xxl={12}>
						<div>
							<Space className={styles.infoWrapper}>
								<FontAwesomeIcon icon={faDroplet as IconProp} className={styles.highlightIcon} />
								Estimated Consumption
							</Space>
							<br />
							{arrivalDate != null ? (
								<>
									{(showNormalConsumption) && (
										seaPassageConsumption?.normal.map((cons) => {
											return (
												<div>
													<b>{`${cons.fuelType}: `}</b>
													{`${formatNumber(cons.consumption, { separateThousands: true }, 2)} MT`}
												</div>
											);
										})
									)}
									{(showEcaConsumption) && (
										seaPassageConsumption?.eca.map((cons) => {
											return (
												<div>
													<b>{`${cons.fuelType}: `}</b>
													{`${formatNumber(cons.consumption, { separateThousands: true }, 2)} MT`}
												</div>
											);
										})
									)}
									{(!showEcaConsumption && !showNormalConsumption) && (
										<EmptyText />
									)}
								</>
							) : 'N/A'}
						</div>
					</Col>
					<Col md={12} xl={24} xxl={12}>
						<div>
							<Space className={styles.infoWrapper}>
								<FontAwesomeIcon icon={faLeaf as IconProp} className={styles.highlightIcon} />
								EU Compliance
							</Space>
							<div className={styles.euComplianceWrapper}>
								<EuComplianceApply
									percent={euCompliancePercent}
									euEtsApply={euEtsApply}
									fuelEuApply={fuelEuApply}
									euEtsLoading={updatingApply === 'euEts'}
									fuelEuLoading={updatingApply === 'fuelEu'}
									disabled={euCompliancePercent === 0}
									onEuEtsChange={(checked) => onApplyChange('euEts', checked)}
									onFuelEuChange={(checked) => onApplyChange('fuelEu', checked)}
									fuelEuEnabled={voyageDetails.fuelEuEnabled}
								/>
							</div>
						</div>
					</Col>
				</Row>
				<Card extra={saveButton}>
					{arrivalDate != null && (
						<div className={styles.settingsContainer}>
							<Typography.Title level={5}>Settings</Typography.Title>
							<Row gutter={[16, 16]}>
								<Col span={12}>
									<b>Allow Panama</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('allowPanamaTransit', value)}
										checked={seaPassageSettings.allowPanamaTransit}
									/>
								</Col>
								<Col span={12}>
									<b>Allow Suez</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('allowSuezTransit', value)}
										checked={seaPassageSettings.allowSuezTransit}
									/>
								</Col>
								<Col span={12}>
									<b>Allow Kiel</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('allowKielTransit', value)}
										checked={seaPassageSettings.allowKielTransit}
									/>
								</Col>
								<Col span={12}>
									<b>Allow Martin Garcia</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('allowMartinGarciaTransit', value)}
										checked={seaPassageSettings.allowMartinGarciaTransit}
									/>
								</Col>
								<Col span={12}>
									<b>Allow Mitre</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('allowMitreTransit', value)}
										checked={seaPassageSettings.allowMitreTransit}
									/>
								</Col>
								<Col span={12}>
									<b>Allow Great Belt (Storebælt)</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('allowGreatBeltTransit', value)}
										checked={seaPassageSettings.allowGreatBeltTransit}
									/>
								</Col>
								<Col span={12}>
									<b>Allow Danish Sound (Øresund)</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('allowDanishSoundTransit', value)}
										checked={seaPassageSettings.allowDanishSoundTransit}
									/>
								</Col>
								<Col span={12}>
									<b>Allow Torres Strait</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('allowTorresStraitTransit', value)}
										checked={seaPassageSettings.allowTorresStraitTransit}
									/>
								</Col>
								<Col span={12}>
									<b>Avoid ECA</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('avoidSeca', value)}
										checked={seaPassageSettings.avoidSeca}
									/>
								</Col>
								<Col span={24}>
									<b>Avoid HRA</b>
									<br />
									<Switch
										disabled={routeLoading}
										onChange={(value) => onUpdateSeaPassage('avoidHRA', value)}
										checked={seaPassageSettings.avoidHRA}
									/>
								</Col>
								<Col span={12}>
									<b>Active speed pair</b>
									<br />
									<Select
										disabled={!(localSeaPassage.estimated || localSeaPassage.inProgress)}
										value={localSeaPassage.activeConsumptionSetId}
										onChange={(value) => onUpdateSeaPassage('activeConsumptionSetId', Number(value))}
										optionFilterProp="title"
										popupMatchSelectWidth={false}
										options={renderConsumptionSetOptions(consumptionSets ?? [])}
									/>
								</Col>
								<Col span={12}>
									<b>Sea Margin</b>
									<br />
									<NumericInput
										disabled={routeLoading || !(localSeaPassage.estimated || localSeaPassage.inProgress)}
										onChange={(value) => onUpdateSeaPassage('seaMargin', value)}
										value={seaPassageSettings.seaMargin}
										addonAfter="%"
									/>
								</Col>
							</Row>
						</div>
					)}
				</Card>
			</Col>
			<Col md={24} xl={16}>
				<div className={styles.map}>
					{map}
				</div>
			</Col>
		</Row>
	);
};

export default SeaPassageSummary;
